/**
 * TaskDetailsHeader Component
 *
 * Renders the header section of the task details view including:
 * - Back navigation button
 * - Task title
 * - Project link tag (if applicable)
 * - Status selector
 * - Copy link and edit buttons
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button, Row, Col, Tooltip, Select, message } from 'antd';
import {
  EditOutlined,
  LeftOutlined,
  LinkOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { statusTypes } from '../../configs/task.config';
import { copyTaskLink } from '../../utils/task.utils';
import '../../styles/index.scss';

const TaskDetailsHeader = ({
  currentTask,
  currentTaskId,
  history,
  level,
  windowWidth,
  updateTaskLoading,
  taskLoading,
  onStatusUpdate,
}) => {
  const isMobile = windowWidth < 768;
  const isTabletOrSmaller = windowWidth < 1200;

  return (
    <>
      {/* Back Navigation */}
      <div className="task-details-back">
        <Button
          onClick={() => {
            const previousPath = document.referrer;
            const cameFromProject = previousPath.includes('/tasks/projects/details/');

            if (currentTask?.project?.id && (cameFromProject || history.length <= 2)) {
              history.push(`/tasks/projects/details/${currentTask.project.id}`);
            } else {
              history.goBack();
            }
          }}
        >
          <LeftOutlined />
          <Text strong>Back</Text>
        </Button>
      </div>

      {/* Header Row */}
      <div className="task-details-header-row">
        {/* Title Area */}
        <div className="task-details-title-area">
          <h1 className="task-details-title">
            {currentTask && currentTask.name}
          </h1>
          {currentTask?.project && (
            <div
              className="task-details-project-tag"
              onClick={() => history.push(`/tasks/projects/details/${currentTask.project.id}`)}
            >
              <LinkOutlined />
              Project: {currentTask.project.name}
            </div>
          )}
        </div>

        {/* Actions */}
        {level !== 'CHANNEL_PARTNER' && (
          <div className="task-details-actions">
            <Select
              className="task-details-status-select"
              value={currentTask && currentTask.status}
              placeholder="Update Status"
              onChange={onStatusUpdate}
              loading={updateTaskLoading || taskLoading}
              disabled={updateTaskLoading || taskLoading}
            >
              {Object.keys(statusTypes).map((status) => (
                <Select.Option key={statusTypes[status]} value={statusTypes[status]}>
                  {status}
                </Select.Option>
              ))}
            </Select>

            <div className="task-details-action-buttons">
              <Tooltip title="Copy Task Link">
                <Button
                  className="task-details-copy-btn"
                  onClick={() => copyTaskLink(currentTaskId, message)}
                  icon={<CopyOutlined />}
                />
              </Tooltip>
              <Button
                className="task-details-edit-btn"
                type="primary"
                onClick={() => history.replace(`/tasks/form/${currentTaskId}`)}
              >
                <EditOutlined /> Edit Task
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

TaskDetailsHeader.propTypes = {
  currentTask: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    status: PropTypes.string,
    project: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  }),
  currentTaskId: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
  level: PropTypes.string,
  windowWidth: PropTypes.number.isRequired,
  updateTaskLoading: PropTypes.bool,
  taskLoading: PropTypes.bool,
  onStatusUpdate: PropTypes.func.isRequired,
};

TaskDetailsHeader.defaultProps = {
  currentTask: null,
  level: null,
  updateTaskLoading: false,
  taskLoading: false,
};

export default TaskDetailsHeader;
